import type {
  ChatProvider,
  ChatProviderWithExtraOptions,
  EmbedProvider,
  EmbedProviderWithExtraOptions,
  SpeechProvider,
  SpeechProviderWithExtraOptions,
  TranscriptionProvider,
  TranscriptionProviderWithExtraOptions,
} from '@xsai-ext/shared-providers'
import type { ProgressInfo } from '@xsai-transformers/shared/types'

import type { ClonedVoice } from '../types/voice'
import type { ProviderErrorInfo } from '../utils/provider-error'
import type { EndpointKind } from './endpoint-kind'

import { computedAsync, useDebounceFn, useLocalStorage } from '@vueuse/core'
import {
  createOpenAI,
} from '@xsai-ext/providers-cloud'
import { listModels } from '@xsai/model'
import { defineStore, getActivePinia } from 'pinia'
import { computed, reactive, ref, toValue, watch } from 'vue'

import { listModels as elevenlabsListModels } from '../services/elevenlabs'
import { getTranslator } from '../utils/i18n-bridge'
import { serializeProviderError } from '../utils/provider-error'
import { extractModelCapabilities } from '../utils/vision'
import { buildOpenAICompatibleProvider } from './providers/openai-compatible-builder'

export interface ProviderMetadata {
  id: string
  order?: number
  category: 'chat' | 'embed' | 'speech'
  tasks: string[]
  nameKey: string // i18n key for provider name
  name: string // Default name (fallback)
  localizedName?: string
  descriptionKey: string // i18n key for description
  description: string // Default description (fallback)
  localizedDescription?: string
  configured?: boolean
  /**
   * Indicates whether the provider is available.
   * If not specified, the provider is always available.
   *
   * May be specified when any of the following criteria is required:
   *
   * Platform requirements:
   *
   * - app-* providers are only available on desktop, this is responsible for Tauri runtime checks
   * - web-* providers are only available on web, this means Node.js and Tauri should not be imported or used
   *
   * System spec requirements:
   *
   * - may requires WebGPU / NVIDIA / other types of GPU,
   *   on Web, WebGPU will automatically compiled to use targeting GPU hardware
   * - may requires significant amount of GPU memory to run, especially for
   *   using of small language models within browser or Tauri app
   * - may requires significant amount of memory to run, especially for those
   *   non-WebGPU supported environments.
   */
  isAvailableBy?: () => Promise<boolean> | boolean
  /**
   * Iconify JSON icon name for the provider.
   *
   * Icons are available for most of the AI provides under @proj-airi/lobe-icons.
   */
  icon?: string
  iconColor?: string
  /**
   * In case of having image instead of icon, you can specify the image URL here.
   */
  iconImage?: string
  defaultOptions?: () => Record<string, unknown>
  createProvider: (
    config: Record<string, unknown>
  ) =>
    | ChatProvider
    | ChatProviderWithExtraOptions
    | EmbedProvider
    | EmbedProviderWithExtraOptions
    | SpeechProvider
    | SpeechProviderWithExtraOptions
    | Promise<ChatProvider>
    | Promise<ChatProviderWithExtraOptions>
    | Promise<EmbedProvider>
    | Promise<EmbedProviderWithExtraOptions>
    | Promise<SpeechProvider>
    | Promise<SpeechProviderWithExtraOptions>
  capabilities: {
    listModels?: (config: Record<string, unknown>) => Promise<ModelInfo[]>
    listVoices?: (config: Record<string, unknown>) => Promise<VoiceInfo[]>
    loadModel?: (config: Record<string, unknown>, hooks?: { onProgress?: (progress: ProgressInfo) => Promise<void> | void }) => Promise<void>
  }
  validators: {
    validateProviderConfig: (config: Record<string, unknown>) => Promise<{
      errors: unknown[]
      reason: string
      valid: boolean
    }> | {
      errors: unknown[]
      reason: string
      valid: boolean
    }
  }
}

export type ProviderCredentials = {
  apiKey?: string
  baseUrl?: string
  accountId?: string
  /** 端点类型 override；不填=auto（按模型名推断），详见 endpoint-kind.ts */
  endpointKind?: EndpointKind
} & Record<string, unknown>

export interface ModelInfo {
  id: string
  name: string
  provider: string
  description?: string
  capabilities?: string[]
  contextLength?: number
  deprecated?: boolean
}

export interface VoiceInfo {
  id: string
  name: string
  provider: string
  compatibleModels?: string[]
  description?: string
  gender?: string
  deprecated?: boolean
  previewURL?: string
  languages: {
    code: string
    title: string
  }[]
}

export interface CustomProviderEntry {
  id: string
  name: string
  /** 端点类型；不填=auto（按模型名推断），详见 endpoint-kind.ts */
  endpointKind?: EndpointKind
}

export const useProvidersStore = defineStore('providers', () => {
  const providerCredentials = useLocalStorage<Record<string, Record<string, unknown>>>('settings/credentials/providers', {})
  const customProviders = useLocalStorage<CustomProviderEntry[]>('settings/custom-providers', [])
  const t = getTranslator()
  // Centralized provider metadata with provider factory functions
  const providerMetadata = reactive<Record<string, ProviderMetadata>>({
    'openai-compatible': buildOpenAICompatibleProvider({
      id: 'openai-compatible',
      name: 'Custom Provider',
      nameKey: 'settings.pages.providers.provider.openai-compatible.title',
      descriptionKey: 'settings.pages.providers.provider.openai-compatible.description',
      icon: 'i-lobe-icons:openai',
      description: 'Connect to any API that follows the OpenAI specification.',
      creator: createOpenAI,
      validation: ['health'],
    }),
    'neko-teaparty': {
      id: 'neko-teaparty',
      category: 'chat',
      tasks: ['text-generation'],
      nameKey: 'settings.pages.providers.provider.neko-teaparty.title',
      name: 'Neko TeaParty',
      descriptionKey: 'settings.pages.providers.provider.neko-teaparty.description',
      description: 'Neko TeaParty API',
      icon: 'i-solar:cup-hot-bold-duotone',
      iconColor: '#ec4899',
      defaultOptions: () => ({
        baseUrl: 'https://api.nktp.top/v1/',
      }),
      createProvider: async (config) => {
        // Base URL is locked, always use the default
        return createOpenAI((config.apiKey as string || '').trim(), 'https://api.nktp.top/v1/')
      },
      capabilities: {
        listModels: async (config) => {
          const provider = createOpenAI((config.apiKey as string || '').trim(), 'https://api.nktp.top/v1/')
          if (!provider.model) {
            return []
          }
          const models = await listModels({ ...provider.model() })
          return models.map((model: any) => ({
            id: model.id,
            name: model.name || model.display_name || model.id,
            provider: 'neko-teaparty',
            description: model.description || '',
            capabilities: extractModelCapabilities(model),
            contextLength: model.context_length || 0,
            deprecated: false,
          }))
        },
      },
      validators: {
        validateProviderConfig: async (config) => {
          if (!config.apiKey) {
            return {
              errors: [new Error('API Key is required')],
              reason: 'API Key is required',
              valid: false,
            }
          }

          // Test API Key validity
          try {
            const response = await fetch('https://api.nktp.top/v1/models', {
              headers: { Authorization: `Bearer ${config.apiKey}` },
            })

            if (!response.ok) {
              return {
                errors: [new Error('Invalid API Key')],
                reason: 'Invalid API Key. Please check your API Key and try again.',
                valid: false,
              }
            }
          }
          catch (e) {
            return {
              errors: [new Error(`Connection failed: ${(e as Error).message}`)],
              reason: `Failed to connect to Neko TeaParty API: ${(e as Error).message}`,
              valid: false,
            }
          }

          return {
            errors: [],
            reason: '',
            valid: true,
          }
        },
      },
    },
    'minimax-speech': {
      id: 'minimax-speech',
      category: 'speech',
      tasks: ['text-to-speech', 'voice-cloning'],
      nameKey: 'settings.pages.providers.provider.minimax-speech.title',
      name: 'MiniMax 语音克隆（国际版）',
      descriptionKey: 'settings.pages.providers.provider.minimax-speech.description',
      description: 'platform.minimax.io',
      icon: 'i-carbon:music',
      iconColor: '#6366f1',
      defaultOptions: () => ({
        baseUrl: 'https://api.minimax.io',
        model: 'speech-2.8-hd',
      }),
      createProvider: async (config) => {
        // MiniMax provider implementation
        const provider: SpeechProvider = {
          speech: (model: string) => {
            return {
              baseURL: (config.baseUrl as string) || 'https://api.minimax.io',
              headers: {
                Authorization: `Bearer ${config.apiKey}`,
              },
              model: model || (config.model as string) || 'speech-2.6-hd',
            }
          },
        }
        return provider
      },
      capabilities: {
        listVoices: async () => {
          // 从声音库加载声音（仅 MiniMax 的声音）
          const { useVoiceLibraryStore } = await import('./voice-library')
          const voiceLibraryStore = useVoiceLibraryStore()
          const voices = voiceLibraryStore.allVoices

          return voices
            .filter((voice: ClonedVoice) => !voice.metadata?.provider || voice.metadata.provider === 'minimax-speech')
            .map((voice: ClonedVoice) => ({
              id: voice.id,
              name: voice.name,
              provider: 'minimax-speech',
              languages: voice.language ? [{ code: voice.language, title: voice.language }] : [],
              previewURL: voice.previewUrl,
            }))
        },
      },
      validators: {
        validateProviderConfig: (config) => {
          const errors = [
            !config.apiKey && new Error('API Key is required.'),
          ].filter(Boolean)

          return {
            errors,
            reason: errors.filter(e => e).map(e => String(e)).join(', ') || '',
            valid: !!config.apiKey,
          }
        },
      },
    },
    'minimax-speech-cn': {
      id: 'minimax-speech-cn',
      category: 'speech',
      tasks: ['text-to-speech', 'voice-cloning'],
      nameKey: 'settings.pages.providers.provider.minimax-cn.title',
      name: 'MiniMax 语音克隆（国内版）',
      descriptionKey: 'settings.pages.providers.provider.minimax-cn.description',
      description: 'platform.minimax.chat',
      icon: 'i-carbon:music',
      iconColor: '#8b5cf6',
      defaultOptions: () => ({
        baseUrl: 'https://api.minimaxi.com',
        model: 'speech-2.8-hd',
      }),
      createProvider: async (config) => {
        const provider: SpeechProvider = {
          speech: (model: string) => {
            return {
              baseURL: (config.baseUrl as string) || 'https://api.minimaxi.com',
              headers: {
                Authorization: `Bearer ${config.apiKey}`,
              },
              model: model || (config.model as string) || 'speech-2.6-hd',
            }
          },
        }
        return provider
      },
      capabilities: {
        listVoices: async () => {
          const { useVoiceLibraryStore } = await import('./voice-library')
          const voiceLibraryStore = useVoiceLibraryStore()
          const voices = voiceLibraryStore.allVoices
          return voices
            .filter((voice: ClonedVoice) => voice.metadata?.provider === 'minimax-speech-cn')
            .map((voice: ClonedVoice) => ({
              id: voice.id,
              name: voice.name,
              provider: 'minimax-speech-cn',
              languages: voice.language ? [{ code: voice.language, title: voice.language }] : [],
              previewURL: voice.previewUrl,
            }))
        },
      },
      validators: {
        validateProviderConfig: (config) => {
          const errors = [
            !config.apiKey && new Error('API Key is required.'),
          ].filter(Boolean)
          return {
            errors,
            reason: errors.filter(e => e).map(e => String(e)).join(', ') || '',
            valid: !!config.apiKey,
          }
        },
      },
    },
    'fish-audio': {
      id: 'fish-audio',
      category: 'speech',
      tasks: ['text-to-speech', 'voice-cloning'],
      nameKey: 'settings.pages.providers.provider.fish-audio.title',
      name: 'Fish Audio',
      descriptionKey: 'settings.pages.providers.provider.fish-audio.description',
      description: 'Fish Audio 语音克隆与合成',
      icon: 'i-carbon:music',
      iconColor: '#3b82f6',
      defaultOptions: () => ({
        baseUrl: 'https://api.fish.audio',
        model: 's2.1-pro',
      }),
      createProvider: async (config) => {
        const provider: SpeechProvider = {
          speech: (model: string) => {
            return {
              baseURL: (config.baseUrl as string) || 'https://api.fish.audio',
              headers: {
                'Authorization': `Bearer ${config.apiKey}`,
                'Content-Type': 'application/json',
              },
              model: model || (config.model as string) || 's2.1-pro',
            }
          },
        }
        return provider
      },
      capabilities: {
        listVoices: async () => {
          const { useVoiceLibraryStore } = await import('./voice-library')
          const voiceLibraryStore = useVoiceLibraryStore()
          const voices = voiceLibraryStore.allVoices

          return voices
            .filter((voice: ClonedVoice) => voice.metadata?.provider === 'fish-audio')
            .map((voice: ClonedVoice) => ({
              id: voice.id,
              name: voice.name,
              provider: 'fish-audio',
              languages: voice.language ? [{ code: voice.language, title: voice.language }] : [],
              previewURL: voice.previewUrl,
            }))
        },
      },
      validators: {
        validateProviderConfig: (config) => {
          const errors = [
            !config.apiKey && new Error('API Key is required.'),
          ].filter(Boolean)

          return {
            errors,
            reason: errors.filter(e => e).map(e => String(e)).join(', ') || '',
            valid: !!config.apiKey,
          }
        },
      },
    },
    'elevenlabs': {
      id: 'elevenlabs',
      category: 'speech',
      tasks: ['text-to-speech', 'voice-cloning'],
      nameKey: 'settings.pages.providers.provider.elevenlabs.title',
      name: 'ElevenLabs',
      descriptionKey: 'settings.pages.providers.provider.elevenlabs.description',
      description: 'ElevenLabs 语音克隆与合成',
      icon: 'i-simple-icons:elevenlabs',
      iconColor: '#000000',
      defaultOptions: () => ({
        baseUrl: '/api/elevenlabs',
        model: 'eleven_multilingual_v2',
      }),
      createProvider: async (config) => {
        const provider: SpeechProvider = {
          speech: (model: string) => {
            return {
              baseURL: (config.baseUrl as string) || '/api/elevenlabs',
              headers: {
                'xi-api-key': config.apiKey as string,
                'Content-Type': 'application/json',
              },
              model: model || (config.model as string) || 'eleven_multilingual_v2',
            }
          },
        }
        return provider
      },
      capabilities: {
        listModels: async (config) => {
          if (!config.apiKey)
            return []
          return await elevenlabsListModels({
            apiKey: config.apiKey as string,
            baseUrl: config.baseUrl as string | undefined,
          })
        },
        listVoices: async () => {
          const { useVoiceLibraryStore } = await import('./voice-library')
          const voiceLibraryStore = useVoiceLibraryStore()
          const voices = voiceLibraryStore.allVoices

          return voices
            .filter((voice: ClonedVoice) => voice.metadata?.provider === 'elevenlabs')
            .map((voice: ClonedVoice) => ({
              id: voice.id,
              name: voice.name,
              provider: 'elevenlabs',
              languages: voice.language ? [{ code: voice.language, title: voice.language }] : [],
              previewURL: voice.previewUrl,
            }))
        },
      },
      validators: {
        validateProviderConfig: (config) => {
          const errors = [
            !config.apiKey && new Error('API Key is required.'),
          ].filter(Boolean)

          return {
            errors,
            reason: errors.filter(e => e).map(e => String(e)).join(', ') || '',
            valid: !!config.apiKey,
          }
        },
      },
    },
  })

  // 动态注册自定义服务商到 providerMetadata
  function registerCustomProvider(entry: CustomProviderEntry) {
    providerMetadata[entry.id] = buildOpenAICompatibleProvider({
      id: entry.id,
      name: entry.name,
      nameKey: `custom.provider.${entry.id}.name`,
      descriptionKey: 'settings.pages.providers.provider.openai-compatible.description',
      icon: 'i-solar:server-square-cloud-bold-duotone',
      description: 'Connect to any API that follows the OpenAI specification.',
      creator: createOpenAI,
      validation: ['health'],
    })
  }

  const configuredProviders = ref<Record<string, boolean>>({})
  const validatedCredentials = ref<Record<string, string>>({})
  const validationGenerations = new Map<string, number>()

  // Configuration validation functions
  async function validateProvider(providerId: string): Promise<boolean> {
    const config = providerCredentials.value[providerId]
    if (!config)
      return false

    const configString = JSON.stringify(config || {})
    if (validatedCredentials.value[providerId] === configString && typeof configuredProviders.value[providerId] === 'boolean')
      return configuredProviders.value[providerId]

    const metadata = providerMetadata[providerId]
    if (!metadata)
      return false

    // Always cache the current config string to prevent re-validating the same config
    validatedCredentials.value[providerId] = configString
    const generation = (validationGenerations.get(providerId) || 0) + 1
    validationGenerations.set(providerId, generation)

    const validationResult = await metadata.validators.validateProviderConfig(config)

    if (validationGenerations.get(providerId) !== generation
      || JSON.stringify(providerCredentials.value[providerId] || {}) !== configString) {
      return configuredProviders.value[providerId] ?? false
    }

    configuredProviders.value[providerId] = validationResult.valid

    return validationResult.valid
  }

  // Create computed properties for each provider's configuration status

  // Initialize provider configurations
  function initializeProvider(providerId: string) {
    if (!providerCredentials.value[providerId]) {
      const metadata = providerMetadata[providerId]
      const defaultOptions = metadata.defaultOptions?.() || {}
      providerCredentials.value[providerId] = {
        baseUrl: defaultOptions.baseUrl || '',
      }
    }
  }

  // Initialize all providers
  Object.keys(providerMetadata).forEach(initializeProvider)

  // 注册已保存的自定义服务商
  customProviders.value.forEach((entry) => {
    registerCustomProvider(entry)
    initializeProvider(entry.id)
  })

  // Update configuration status for all providers
  async function updateConfigurationStatus() {
    await Promise.all(Object.keys(providerMetadata).map(async (providerId) => {
      try {
        configuredProviders.value[providerId] = await validateProvider(providerId)
      }
      catch {
        configuredProviders.value[providerId] = false
      }
    }))
  }

  // 配置输入过程中不要每个字符都触发全量网络验证；等待用户停顿后再刷新状态。
  const scheduleConfigurationStatusUpdate = useDebounceFn(() => {
    void updateConfigurationStatus()
  }, 500)
  watch(providerCredentials, scheduleConfigurationStatusUpdate, { deep: true, immediate: true })

  // Available providers (only those that are properly configured)
  const availableProviders = computed(() => Object.keys(providerMetadata).filter(providerId => configuredProviders.value[providerId]))

  // Store available models for each provider
  const availableModels = ref<Record<string, ModelInfo[]>>({})
  const isLoadingModels = ref<Record<string, boolean>>({})
  const modelLoadError = ref<Record<string, ProviderErrorInfo | null>>({})
  // 每个 provider 的 fetch 代号，防止快速改 key 时旧请求后返回盖掉新成功态
  const fetchGenerations = new Map<string, number>()

  function hasCredential(config: Record<string, unknown>): boolean {
    const apiKey = typeof config.apiKey === 'string' ? config.apiKey.trim() : ''
    const baseUrl = typeof config.baseUrl === 'string' ? config.baseUrl.trim() : ''
    const accountId = typeof config.accountId === 'string' ? config.accountId.trim() : ''
    return !!(apiKey || baseUrl || accountId)
  }

  // Function to fetch models for a specific provider
  async function fetchModelsForProvider(providerId: string) {
    const config = providerCredentials.value[providerId]
    if (!config)
      return []

    const metadata = providerMetadata[providerId]
    if (!metadata)
      return []

    // 凭据完全为空（apiKey/baseUrl/accountId 三选一都没）：清空旧数据让 UI 回到 idle
    if (!hasCredential(config)) {
      availableModels.value[providerId] = []
      modelLoadError.value[providerId] = null
      isLoadingModels.value[providerId] = false
      fetchGenerations.set(providerId, (fetchGenerations.get(providerId) || 0) + 1)
      return []
    }

    const generation = (fetchGenerations.get(providerId) || 0) + 1
    fetchGenerations.set(providerId, generation)
    isLoadingModels.value[providerId] = true
    modelLoadError.value[providerId] = null

    try {
      const models = metadata.capabilities.listModels ? await metadata.capabilities.listModels(config) : []

      if (fetchGenerations.get(providerId) !== generation)
        return availableModels.value[providerId] || []

      availableModels.value[providerId] = models.map(model => ({
        id: model.id,
        name: model.name,
        description: model.description,
        capabilities: model.capabilities,
        contextLength: model.contextLength,
        deprecated: model.deprecated,
        provider: providerId,
      }))

      return availableModels.value[providerId]
    }
    catch (error) {
      if (fetchGenerations.get(providerId) !== generation)
        return availableModels.value[providerId] || []

      console.error(`Error fetching models for ${providerId}:`, error)
      availableModels.value[providerId] = []
      modelLoadError.value[providerId] = serializeProviderError(error)
      return []
    }
    finally {
      if (fetchGenerations.get(providerId) === generation)
        isLoadingModels.value[providerId] = false
    }
  }

  // Get models for a specific provider
  function getModelsForProvider(providerId: string) {
    return availableModels.value[providerId] || []
  }

  // Get all available models across all configured providers
  const allAvailableModels = computed(() => {
    const models: ModelInfo[] = []
    for (const providerId of availableProviders.value) {
      models.push(...(availableModels.value[providerId] || []))
    }
    return models
  })

  // Load models for all configured providers
  async function loadModelsForConfiguredProviders() {
    for (const providerId of availableProviders.value) {
      if (providerMetadata[providerId].capabilities.listModels) {
        await fetchModelsForProvider(providerId)
      }
    }
  }
  // 每个服务商独立的模型重拉防抖器，避免用户连续输入 key 时刷接口
  const refetchModelsDebouncers = new Map<string, () => void>()
  function getRefetchModelsDebouncer(providerId: string) {
    let fn = refetchModelsDebouncers.get(providerId)
    if (!fn) {
      fn = useDebounceFn(() => {
        if (providerMetadata[providerId]?.capabilities.listModels) {
          fetchModelsForProvider(providerId)
        }
      }, 400)
      refetchModelsDebouncers.set(providerId, fn)
    }
    return fn
  }

  // 只比较真正影响服务商连通性的字段，避免 enabled/customName 等 UI 字段误触发重拉
  const CONNECTIVITY_KEYS = ['apiKey', 'baseUrl', 'accountId'] as const
  function connectivitySnapshot(cfg: Record<string, unknown> | undefined): string {
    if (!cfg)
      return ''
    const picked: Record<string, unknown> = {}
    for (const key of CONNECTIVITY_KEYS) {
      if (key in cfg)
        picked[key] = cfg[key]
    }
    return JSON.stringify(picked)
  }

  // Watch for credential changes and refetch models accordingly.
  // 注意：deep watch 的 newCreds/oldCreds 是同一个 proxy 引用（Vue 3 已知行为），
  // 直接在回调内对比新旧快照永远相等，导致 changedProviders 始终为空、模型从不重拉。
  // 修法：改为 watch 一个 computed snapshot（每次返回新 plain object），新旧对象不同引用，比较才有意义。
  const credentialConnectivityMap = computed(() =>
    Object.fromEntries(
      Object.keys(providerCredentials.value).map(id => [
        id,
        connectivitySnapshot(providerCredentials.value[id]),
      ]),
    ),
  )

  watch(credentialConnectivityMap, (newMap, oldMap) => {
    if (!oldMap)
      return
    const changedProviders = Object.keys(newMap).filter(
      id => newMap[id] !== (oldMap[id] ?? ''),
    )
    for (const providerId of changedProviders) {
      if (providerMetadata[providerId]?.capabilities.listModels) {
        // 立刻清掉旧错误，避免 UI 展示陈旧的 “获取出错啦”
        modelLoadError.value[providerId] = null
        getRefetchModelsDebouncer(providerId)()
      }
    }
  })

  // 获取本地化名称，自定义服务商直接用 name，不走 i18n
  function localizedNameOf(metadata: ProviderMetadata): string {
    if (customProviders.value.some(p => p.id === metadata.id)) {
      return metadata.name
    }
    return t(metadata.nameKey, metadata.name)
  }

  function localizedDescriptionOf(metadata: ProviderMetadata): string {
    if (customProviders.value.some(p => p.id === metadata.id)) {
      return metadata.description
    }
    return t(metadata.descriptionKey, metadata.description)
  }

  // Function to get localized provider metadata
  function getProviderMetadata(providerId: string) {
    const metadata = providerMetadata[providerId]

    if (!metadata)
      throw new Error(`Provider metadata for ${providerId} not found`)

    return {
      ...metadata,
      localizedName: localizedNameOf(metadata),
      localizedDescription: localizedDescriptionOf(metadata),
    }
  }

  // Get all providers metadata (for settings page)
  const allProvidersMetadata = computed(() => {
    return Object.values(providerMetadata).map(metadata => ({
      ...metadata,
      localizedName: localizedNameOf(metadata),
      localizedDescription: localizedDescriptionOf(metadata),
      configured: configuredProviders.value[metadata.id] || false,
    }))
  })

  // Function to get provider object by provider id
  async function getProviderInstance<R extends
  | ChatProvider
  | ChatProviderWithExtraOptions
  | EmbedProvider
  | EmbedProviderWithExtraOptions
  | SpeechProvider
  | SpeechProviderWithExtraOptions
  | TranscriptionProvider
  | TranscriptionProviderWithExtraOptions,
  >(providerId: string): Promise<R> {
    const config = providerCredentials.value[providerId]
    if (!config)
      throw new Error(`Provider credentials for ${providerId} not found`)

    const metadata = providerMetadata[providerId]
    if (!metadata)
      throw new Error(`Provider metadata for ${providerId} not found`)

    try {
      const instance = await metadata.createProvider(config) as R
      // 把自定义 provider 的端点类型刻印到 provider 实例本身（而非 .chat() 配置，
      // 后者会被 llm.ts 用 ...chatConfig spread 进 streamText body 造成未知字段泄漏）。
      // streamFrom 从 chatProvider 读取此字段作 resolveEndpointKind 的 override。
      const customEntry = customProviders.value.find(p => p.id === providerId)
      // 自定义 provider 优先读 customProviders；内置 provider 从 providerCredentials 读
      const endpointKindOverride = customEntry?.endpointKind
        || providerCredentials.value[providerId]?.endpointKind
        || undefined
      if (endpointKindOverride && endpointKindOverride !== 'auto')
        (instance as unknown as Record<string, unknown>).endpointKind = endpointKindOverride
      return instance
    }
    catch (error) {
      console.error(`Error creating provider instance for ${providerId}:`, error)
      throw error
    }
  }

  const availableProvidersMetadata = computedAsync<ProviderMetadata[]>(async () => {
    const results = await Promise.all(
      allProvidersMetadata.value.map(async (provider) => {
        const p = getProviderMetadata(provider.id)
        const isAvailableBy = p.isAvailableBy || (() => true)
        const isAvailable = await isAvailableBy()
        return isAvailable ? provider : null
      }),
    )
    return results.filter((p): p is NonNullable<typeof p> => p !== null)
  }, [])

  const allChatProvidersMetadata = computed(() => {
    return availableProvidersMetadata.value.filter(metadata => metadata.category === 'chat')
  })

  const allAudioSpeechProvidersMetadata = computed(() => {
    return availableProvidersMetadata.value.filter(metadata => metadata.category === 'speech')
  })

  const configuredChatProvidersMetadata = computed(() => {
    return allChatProvidersMetadata.value.filter(metadata => configuredProviders.value[metadata.id])
  })

  const configuredSpeechProvidersMetadata = computed(() => {
    return allAudioSpeechProvidersMetadata.value.filter(metadata => configuredProviders.value[metadata.id])
  })

  function getProviderConfig(providerId: string) {
    return providerCredentials.value[providerId]
  }

  // 启用/禁用服务商 - 设置为对应类型的 active provider
  async function toggleProvider(providerId: string, enabled: boolean) {
    const metadata = getProviderMetadata(providerId)
    if (!metadata) {
      console.warn(`Provider ${providerId} not found`)
      return
    }

    // 根据 category 动态导入对应的 store 并设置 active provider
    if (enabled) {
      if (metadata.category === 'chat') {
        const { useConsciousnessStore } = await import('./modules/consciousness')
        const consciousnessStore = useConsciousnessStore()
        // 使用 $patch 方法更安全地更新 store 状态
        consciousnessStore.$patch({
          activeProvider: providerId,
        })
      }
      else if (metadata.category === 'speech') {
        const { useSpeechStore } = await import('./modules/speech')
        const speechStore = useSpeechStore()
        speechStore.$patch({
          activeSpeechProvider: providerId,
        })
      }
    }
    else {
      // 禁用时，将对应类型的 active provider 设为空
      if (metadata.category === 'chat') {
        const { useConsciousnessStore } = await import('./modules/consciousness')
        const consciousnessStore = useConsciousnessStore()
        // 使用 storeToRefs 来安全访问 ref 值
        const currentProvider = toValue(consciousnessStore.activeProvider)
        if (currentProvider === providerId) {
          consciousnessStore.$patch({
            activeProvider: '',
          })
        }
      }
      else if (metadata.category === 'speech') {
        const { useSpeechStore } = await import('./modules/speech')
        const speechStore = useSpeechStore()
        const currentProvider = toValue(speechStore.activeSpeechProvider)
        if (currentProvider === providerId) {
          speechStore.$patch({
            activeSpeechProvider: '',
          })
        }
      }
    }
  }

  // Helper function to get store instance by ID from Pinia
  function getStoreById(storeId: string) {
    const pinia = getActivePinia()
    if (!pinia)
      return null
    // Access Pinia's internal store registry
    return (pinia as any)._s.get(storeId)
  }

  // 检查服务商是否已启用 - 检查是否是当前 active 的 provider
  function isProviderEnabled(providerId: string): boolean {
    const metadata = providerMetadata[providerId]
    if (!metadata)
      return false

    // 根据 category 检查对应 store 的 active provider
    try {
      if (metadata.category === 'chat') {
        const consciousnessStore = getStoreById('consciousness')
        return consciousnessStore?.activeProvider === providerId
      }
      else if (metadata.category === 'speech') {
        const speechStore = getStoreById('speech')
        return speechStore?.activeSpeechProvider === providerId
      }
    }
    catch (error) {
      console.warn('Error checking provider enabled status:', error)
      return false
    }

    return false
  }

  // 获取已启用的服务商（按类型） - 基于 activeProvider
  const enabledChatProvider = computed(() => {
    const consciousnessStore = getStoreById('consciousness')
    const activeId = consciousnessStore?.activeProvider
    return allChatProvidersMetadata.value.find(p => p.id === activeId)
  })

  const enabledSpeechProvider = computed(() => {
    const speechStore = getStoreById('speech')
    const activeId = speechStore?.activeSpeechProvider
    return allAudioSpeechProvidersMetadata.value.find(p => p.id === activeId)
  })

  // 检查是否启用了 Neko TeaParty - 检查是否是当前 active chat provider
  const isNekoTeaPartyEnabled = computed(() => {
    const consciousnessStore = getStoreById('consciousness')
    return consciousnessStore?.activeProvider === 'neko-teaparty'
  })

  // 过滤已启用的服务商 - 返回当前 active 的 provider（数组形式以兼容现有代码）
  const enabledChatProvidersMetadata = computed(() => {
    const provider = enabledChatProvider.value
    return provider ? [provider] : []
  })

  const enabledSpeechProvidersMetadata = computed(() => {
    const provider = enabledSpeechProvider.value
    return provider ? [provider] : []
  })

  // 自定义服务商 CRUD 方法
  function addCustomProvider(name: string): string {
    const id = `custom-${Date.now()}`
    const entry: CustomProviderEntry = { id, name }
    customProviders.value = [...customProviders.value, entry]
    registerCustomProvider(entry)
    initializeProvider(id)
    return id
  }

  function removeCustomProvider(id: string) {
    // 如果是当前激活的服务商，先禁用
    if (isProviderEnabled(id)) {
      toggleProvider(id, false)
    }
    // 从自定义服务商列表中移除
    customProviders.value = customProviders.value.filter(p => p.id !== id)
    // 从 providerMetadata 中移除
    delete providerMetadata[id]
    // 清理 credentials
    const creds = { ...providerCredentials.value }
    delete creds[id]
    providerCredentials.value = creds
    // 清理配置状态缓存
    delete configuredProviders.value[id]
    delete validatedCredentials.value[id]
    // 清理模型相关缓存
    delete availableModels.value[id]
    delete isLoadingModels.value[id]
    delete modelLoadError.value[id]
  }

  function isCustomProvider(id: string): boolean {
    return customProviders.value.some(p => p.id === id)
  }

  function renameCustomProvider(id: string, name: string) {
    customProviders.value = customProviders.value.map(p =>
      p.id === id ? { ...p, name } : p,
    )
    if (providerMetadata[id]) {
      providerMetadata[id].name = name
    }
  }

  // 更新自定义 provider 的端点类型；传 undefined/'auto' 等价于清除（按模型名推断）
  function updateCustomProviderEndpointKind(id: string, endpointKind: EndpointKind | undefined) {
    customProviders.value = customProviders.value.map(p =>
      p.id === id ? { ...p, endpointKind } : p,
    )
  }

  // 更新内置 provider 的端点类型（存入 providerCredentials）；自动识别自定义/内置
  function updateProviderEndpointKind(id: string, kind: EndpointKind | undefined) {
    if (customProviders.value.some(p => p.id === id)) {
      updateCustomProviderEndpointKind(id, kind)
      return
    }
    if (!providerCredentials.value[id])
      providerCredentials.value[id] = {}
    if (kind && kind !== 'auto')
      providerCredentials.value[id].endpointKind = kind
    else
      delete providerCredentials.value[id].endpointKind
  }

  return {
    providers: providerCredentials,
    getProviderConfig,
    availableProviders,
    configuredProviders,
    providerMetadata,
    getProviderMetadata,
    allProvidersMetadata,
    initializeProvider,
    validateProvider,
    availableModels,
    isLoadingModels,
    modelLoadError,
    fetchModelsForProvider,
    getModelsForProvider,
    allAvailableModels,
    loadModelsForConfiguredProviders,
    getProviderInstance,
    availableProvidersMetadata,
    allChatProvidersMetadata,
    allAudioSpeechProvidersMetadata,
    configuredChatProvidersMetadata,
    configuredSpeechProvidersMetadata,
    // 新增：启用/禁用相关
    toggleProvider,
    isProviderEnabled,
    enabledChatProvider,
    enabledSpeechProvider,
    isNekoTeaPartyEnabled,
    enabledChatProvidersMetadata,
    enabledSpeechProvidersMetadata,
    // 自定义服务商
    customProviders,
    addCustomProvider,
    removeCustomProvider,
    isCustomProvider,
    renameCustomProvider,
    updateCustomProviderEndpointKind,
    updateProviderEndpointKind,
  }
})
